/* @jsxImportSource @emotion/react */

import React, { useCallback, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/browser'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import { buildGrainBackground } from '@slite/components'
import { SearchIcon } from '@slite/icons'

import { Button } from '../../components/Button/Button'
import { Section } from '../../components/Section'
import { SlitePage } from '../../components/SlitePage'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import type { Data } from './+data'
import stroke01 from './images/stroke01.png'
import stroke02 from './images/stroke02.png'
import stroke03 from './images/stroke03.png'
import {
  fetchAllLearnPosts,
  LEARN_FEATURED_COUNT,
  LEARN_INDEX_PAGE_SIZE,
} from './postsQuery'
import './style.css'

type Routes = ReturnType<typeof useRoutes>
type Post = Data['posts'][number]

// Cover colors match the templates page items (see getTemplateColorFromRemote
// in templates/constants); each cover gets its own white stroke overlay.
// White type on the saturated orange, dark ink on the lighter pastels.
const BOOK_COVERS = [
  {
    color: 'var(--slite-orange-300)',
    strokeUrl: stroke01,
    textClass: 'full-white',
  },
  {
    color: 'var(--moon-dust-darkest)',
    strokeUrl: stroke02,
    textClass: 'shade-05',
  },
  {
    color: 'var(--slite-blue-300)',
    strokeUrl: stroke03,
    textClass: 'shade-05',
  },
]
if (BOOK_COVERS.length !== LEARN_FEATURED_COUNT) {
  throw new Error(
    'BOOK_COVERS must define one cover per featured post (LEARN_FEATURED_COUNT)'
  )
}

const DIN_PORTRAIT_RATIO = '1 / 1.4142'

const GRAIN_BACKGROUND = buildGrainBackground()

function FeaturedBook({
  post,
  routes,
  cover,
}: {
  post: Post
  routes: Routes
  cover: (typeof BOOK_COVERS)[number]
}) {
  const intl = useIntl()
  const formattedDate = intl.formatDate(post.date, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  return (
    <a
      href={routes.learn.detail({ slug: post.slug.current })}
      className="block height-full"
      style={{ color: 'inherit', textDecoration: 'none' }}
    >
      <div
        className="learn-book-cover border-small border-full-white height-full padding-24 flexbox flexbox-column text-align-left"
        style={{
          borderRadius: 8,
          aspectRatio: DIN_PORTRAIT_RATIO,
          backgroundColor: cover.color,
          backgroundImage: `url(${cover.strokeUrl}), ${GRAIN_BACKGROUND}`,
          backgroundSize: 'cover, auto',
          backgroundRepeat: 'no-repeat, repeat',
          backgroundBlendMode: 'normal, screen',
        }}
      >
        <WebsiteText
          variant="body"
          className={classNames(
            cover.textClass,
            'font-size-8 font-weight-semibold'
          )}
          css={{ letterSpacing: '0.5px', opacity: 0.85 }}
        >
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {post.author} · {formattedDate}
        </WebsiteText>
        <WebsiteText
          variant="body"
          htmlTag="h2"
          className={classNames(cover.textClass, 'margin-top-12')}
        >
          {post.title}
        </WebsiteText>
        <WebsiteText
          variant="body"
          className={classNames(
            cover.textClass,
            'learn-book-read-now font-weight-semibold margin-top-auto'
          )}
        >
          <FormattedMessage id="learn.book.readNow" defaultMessage="Read now" />
        </WebsiteText>
      </div>
    </a>
  )
}

// The stagger index must be relative to the batch a row mounts with (the CSS
// caps the resulting delay), so appended rows always cascade from zero
function rowEnterStyle(staggerIndex: number) {
  return {
    '--learn-row-index': Math.max(0, staggerIndex),
  } as React.CSSProperties
}

function PostRow({
  post,
  routes,
  index,
  animated,
}: {
  post: Post
  routes: Routes
  index: number
  animated: boolean
}) {
  const intl = useIntl()
  const formattedDate = intl.formatDate(post.date, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  return (
    <a
      href={routes.learn.detail({ slug: post.slug.current })}
      className={classNames(
        animated && 'learn-row-enter',
        'block padding-vertical-20 border-bottom-small border-alpha-shade-50 padding-20 background-full-white-on-hover transition'
      )}
      style={{
        color: 'inherit',
        textDecoration: 'none',
        ...rowEnterStyle(index),
      }}
    >
      <div className="flexbox flexbox-space-between flexbox-align-center flexbox-column-gap-24 flexbox-column-small flexbox-align-start-small flexbox-row-gap-4-small">
        <WebsiteText variant="body" className="font-weight-medium shade-05">
          {post.title}
        </WebsiteText>
        <WebsiteText
          variant="body"
          className="shade-05 font-size-7 flexbox-no-shrink"
        >
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {post.author} · {formattedDate}
        </WebsiteText>
      </div>
    </a>
  )
}

function LearnIndexPage(props: Data) {
  const intl = useIntl()
  const routes = useRoutes()
  const [searchQuery, setSearchQuery] = useState('')
  const [visibleCount, setVisibleCount] = useState(LEARN_INDEX_PAGE_SIZE)
  const [allPosts, setAllPosts] = useState<Data['posts'] | null>(null)
  const [isLoadingAllPosts, setIsLoadingAllPosts] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const hasRequestedAllPosts = useRef(false)

  const { sanity, language } = props
  const loadAllPosts = useCallback(() => {
    if (hasRequestedAllPosts.current) return
    hasRequestedAllPosts.current = true
    setIsLoadingAllPosts(true)
    setLoadError(false)
    fetchAllLearnPosts(sanity, language)
      .then(setAllPosts)
      .catch((error) => {
        Sentry.captureException(error)
        setLoadError(true)
        // allow a retry from the error state or the next search keystroke
        hasRequestedAllPosts.current = false
      })
      .finally(() => setIsLoadingAllPosts(false))
  }, [sanity, language])

  const title = intl.formatMessage({
    id: 'learn.index.title',
    defaultMessage: 'Learn actionable tips and resources | Slite',
  })
  const description = intl.formatMessage({
    id: 'learn.index.description',
    defaultMessage:
      'Discover actionable tips and resources to move forward faster.',
  })

  const posts = allPosts ?? props.posts
  const normalizedQuery = searchQuery.trim().toLowerCase()

  const filteredPosts = useMemo(() => {
    if (!normalizedQuery) return posts
    const queryWords = normalizedQuery.split(/\s+/)
    return posts.filter((post) => {
      const haystack = `${post.title} ${post.description ?? ''}`.toLowerCase()
      return queryWords.every((word) => haystack.includes(word))
    })
  }, [posts, normalizedQuery])

  const isSearching = normalizedQuery.length > 0
  const featuredPosts = !isSearching ? posts.slice(0, LEARN_FEATURED_COUNT) : []
  const listPosts = isSearching
    ? filteredPosts
    : posts.slice(LEARN_FEATURED_COUNT)
  const visiblePosts = listPosts.slice(0, visibleCount)
  const hasUnloadedPosts =
    allPosts === null && props.totalCount > props.posts.length
  const hasMore =
    visibleCount < listPosts.length || (!isSearching && hasUnloadedPosts)
  // While searching without the full catalogue, an empty result is not proof
  // of "no matches" — surface the failed load instead of a false empty state
  const showLoadError = isSearching && allPosts === null && loadError

  // Index of the first row of the batch currently mounting, so appended rows
  // start their enter cascade at zero delay instead of continuing the count
  // from the rows already on screen
  const batchStartRef = useRef(0)

  const loadMore = () => {
    batchStartRef.current = visiblePosts.length
    setVisibleCount((count) => count + LEARN_INDEX_PAGE_SIZE)
    loadAllPosts()
  }

  return (
    <SlitePage
      title={title}
      description={description}
      ogTitle={title}
      ogDescription={description}
      canonicalUrl={routes.learn.index()}
      background="moon-dust-lighter"
    >
      <Section className="text-align-center" background="moon-dust-lighter">
        <div className="grid-12">
          <div className="grid-span-8-large grid-offset-2-large grid-span-12 flexbox flexbox-column flexbox-align-center">
            <WebsiteText variant="display" className="margin-bottom-16">
              <FormattedMessage
                id="learn.title"
                defaultMessage="What do you want to learn?"
              />
            </WebsiteText>
            <WebsiteText
              variant="body"
              className="font-weight-semibold margin-bottom-32"
              style={{ maxWidth: 500 }}
            >
              <FormattedMessage
                id="learn.subtitle"
                defaultMessage="Discover actionable tips and resources to move forward faster."
              />
            </WebsiteText>
            <div
              className="position-relative width-full"
              style={{ maxWidth: 560 }}
            >
              <input
                aria-label={intl.formatMessage({
                  id: 'learn.search.ariaLabel',
                  defaultMessage: 'Search articles',
                })}
                type="text"
                placeholder={intl.formatMessage({
                  id: 'learn.search.placeholder',
                  defaultMessage: 'Search articles',
                })}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setVisibleCount(LEARN_INDEX_PAGE_SIZE)
                  batchStartRef.current = 0
                  if (e.target.value.trim()) loadAllPosts()
                }}
                className="width-full border-small radius-largest padding-vertical-20 font-size-5 background-white border-alpha-shade-40 border-shade-30-on-hover transition"
                style={{ paddingLeft: 24, paddingRight: 56 }}
              />
              <SearchIcon
                size={20}
                className="position-absolute shade-30"
                style={{ right: 28, top: '50%', transform: 'translateY(-50%)' }}
              />
            </div>
          </div>
        </div>
      </Section>

      <Section containerTopPadding={0} background="moon-dust-lighter">
        <div className="grid-12">
          <div className="grid-span-10-large grid-offset-1-large grid-span-12">
            {featuredPosts.length > 0 && (
              <div className="margin-bottom-60 grid-12 grid-gap-32 padding-horizontal-20">
                {featuredPosts.map((post, index) => (
                  <div
                    key={post._id}
                    className="learn-row-enter grid-span-4 grid-span-12-small"
                    style={rowEnterStyle(index)}
                  >
                    <FeaturedBook
                      post={post}
                      routes={routes}
                      cover={BOOK_COVERS[index % BOOK_COVERS.length]}
                    />
                  </div>
                ))}
              </div>
            )}

            {listPosts.length > 0 ? (
              <div>
                {visiblePosts.map((post, index) => (
                  <PostRow
                    key={post._id}
                    post={post}
                    routes={routes}
                    index={index - batchStartRef.current}
                    animated={!isSearching}
                  />
                ))}
                {hasMore && (
                  <div className="flexbox flexbox-justify-center margin-top-40">
                    <Button
                      variant="secondary"
                      disabled={isLoadingAllPosts}
                      onClick={loadMore}
                    >
                      <FormattedMessage
                        id="learn.loadMore"
                        defaultMessage="Load more"
                      />
                    </Button>
                  </div>
                )}
              </div>
            ) : isLoadingAllPosts ? null : showLoadError ? (
              <div className="flexbox flexbox-column flexbox-align-center padding-vertical-60">
                <WebsiteText
                  variant="body"
                  className="text-align-center shade-20 margin-bottom-20"
                >
                  <FormattedMessage
                    id="learn.search.loadFailed"
                    defaultMessage="We couldn't load all articles to search through."
                  />
                </WebsiteText>
                <Button variant="secondary" onClick={loadAllPosts}>
                  <FormattedMessage
                    id="learn.search.retry"
                    defaultMessage="Try again"
                  />
                </Button>
              </div>
            ) : (
              <WebsiteText
                variant="body"
                className="text-align-center shade-20 padding-vertical-60"
              >
                <FormattedMessage
                  id="learn.search.noResults"
                  defaultMessage="No articles match your search."
                />
              </WebsiteText>
            )}
          </div>
        </div>
      </Section>
    </SlitePage>
  )
}

export default function Page() {
  const data = useData<Data>()
  return <LearnIndexPage {...data} />
}
